import React, { useState, useEffect } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Box, Typography } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  HIERARCHY_CHEVRON_W,
  HIERARCHY_COLS,
} from "../../pages/assetSnapshot/hierarchyLayout";

export default function RecursiveTreeView(props) {
  const { isDark = false, data: dataProp } = props;
  const [expandedItems, setExpandedItems] = useState(() =>
    Array.isArray(dataProp?.defaultExpanded) ? dataProp.defaultExpanded : []
  );
  const [selectedItems, setSelectedItems] = useState(
    () => dataProp?.defaultSelected ?? null
  );

  const greenColor = "#A0C46D";
  const greenDark = "#4d7c2c";
  const hoverBg = isDark ? "rgba(160, 196, 109, 0.08)" : "#f1f5f9";
  const selectedBg = isDark
    ? "rgba(160, 196, 109, 0.16)"
    : "rgba(220, 252, 231, 0.85)";
  const selectedHoverBg = isDark
    ? "rgba(160, 196, 109, 0.24)"
    : "rgba(187, 247, 208, 0.9)";
  const selectedBorder = isDark ? greenColor : greenDark;
  const borderColor = isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0";
  const rowBorder = isDark ? "transparent" : "#f1f5f9";
  const headerBg = isDark ? "rgba(15, 40, 60, 0.65)" : "#ffffff";
  const textColor = isDark ? "#e2e8f0" : "#0f172a";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const chevronColor = isDark ? greenColor : "#64748b";

  const classes = {
    root: {
      flexGrow: 1,
      width: "100%",
      textAlign: "left",
      "& .MuiTreeItem-group": {
        marginLeft: 0,
        paddingLeft: 0,
        borderLeft: "none",
      },
      "& .MuiTreeItem-content": {
        padding: "1px 4px",
        paddingLeft: "4px !important",
        marginBottom: isDark ? "1px" : "2px",
        borderRadius: "6px",
        justifyContent: "flex-start",
        textAlign: "left",
        minHeight: 26,
        // Keep border width constant so selection doesn't shift Qty vs header
        border: isDark ? "1.5px solid transparent" : `1.5px solid ${rowBorder}`,
        backgroundColor: isDark ? "transparent" : "#ffffff",
        boxSizing: "border-box",
        transition:
          "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          backgroundColor: hoverBg,
          borderColor: isDark ? "transparent" : "#e2e8f0",
        },
        "&.Mui-selected": {
          backgroundColor: selectedBg,
          border: `1.5px solid ${selectedBorder}`,
          boxShadow: isDark
            ? "0 0 0 1px rgba(160, 196, 109, 0.2)"
            : "0 1px 3px rgba(22, 101, 52, 0.12)",
          "&:hover": {
            backgroundColor: selectedHoverBg,
          },
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `inset 0 0 0 2px ${selectedBorder}`,
        },
      },
      "& .MuiTreeItem-label": {
        color: textColor,
        textAlign: "left",
        width: "100%",
        flex: "1 1 auto",
        minWidth: 0,
        paddingLeft: "0 !important",
        paddingRight: "0 !important",
        marginLeft: 0,
        marginRight: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      },
      "& .MuiTreeItem-iconContainer": {
        width: `${HIERARCHY_CHEVRON_W}px`,
        minWidth: `${HIERARCHY_CHEVRON_W}px`,
        maxWidth: `${HIERARCHY_CHEVRON_W}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: "0 !important",
        marginRight: "6px",
        flexShrink: 0,
      },
      "@media (prefers-reduced-motion: reduce)": {
        "& .MuiTreeItem-content": { transition: "none" },
        "& .MuiCollapse-root": { transition: "none !important" },
      },
    },
    label: {
      fontFamily: "Lato, sans-serif",
      fontWeight: 500,
      fontSize: "0.7rem",
      lineHeight: 1.25,
      color: textColor,
      width: "100%",
    },
  };

  const data = React.useMemo(() => dataProp, [dataProp]);

  useEffect(() => {
    if (Array.isArray(data?.defaultExpanded) && data.defaultExpanded.length > 0) {
      setExpandedItems(data.defaultExpanded);
    }
  }, [data?.defaultExpanded]);

  useEffect(() => {
    if (data?.defaultSelected != null) {
      setSelectedItems(data.defaultSelected);
    }
  }, [data?.defaultSelected]);

  const renderTree = (nodes, depth = 0) => {
    if (!nodes || nodes.id == null) return null;
    return (
      <TreeItem
        classes={{ label: classes.label }}
        itemId={String(nodes.id)}
        key={nodes.id}
        label={nodes.label}
      >
        {Array.isArray(nodes.children) && nodes.children.length > 0
          ? nodes.children.map((node) => renderTree(node, depth + 1))
          : null}
      </TreeItem>
    );
  };

  if (!data) {
    return (
      <Box
        role="status"
        aria-label="No hierarchy data"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 120,
          gap: 1,
          py: 2,
        }}
      >
        <AccountTreeIcon
          sx={{
            fontSize: "2rem",
            color: isDark
              ? "rgba(148, 163, 184, 0.3)"
              : "rgba(100, 116, 139, 0.3)",
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: textSecondary, fontWeight: 600 }}
        >
          No hierarchy data available
        </Typography>
      </Box>
    );
  }

  const selectedId =
    selectedItems != null ? String(selectedItems) : undefined;

  const headerCell = {
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: textSecondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        role="row"
        aria-hidden={false}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          px: "4px",
          py: "4px",
          mb: 0.5,
          borderRadius: "6px",
          backgroundColor: headerBg,
          border: `1px solid ${borderColor}`,
          boxShadow: isDark ? "none" : "0 1px 2px rgba(15, 23, 42, 0.04)",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        {/* Matches TreeItem chevron column so Qty lines up with row values */}
        <Box
          component="span"
          sx={{
            width: HIERARCHY_CHEVRON_W,
            minWidth: HIERARCHY_CHEVRON_W,
            flexShrink: 0,
          }}
        />
        <Box
          component="span"
          sx={{
            display: "grid",
            gridTemplateColumns: HIERARCHY_COLS,
            alignItems: "center",
            justifyItems: "stretch",
            gap: "0 6px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box component="span" sx={headerCell}>
            Tag
          </Box>
          <Box component="span" sx={headerCell}>
            Description
          </Box>
          <Box component="span" sx={headerCell}>
            Event
          </Box>
          <Box
            component="span"
            sx={{
              ...headerCell,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            Quantity Picked
          </Box>
          <Box
            component="span"
            sx={{
              ...headerCell,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {" "}
          </Box>
        </Box>
      </Box>

      <SimpleTreeView
        sx={classes.root}
        aria-label="Asset hierarchy tree"
        itemChildrenIndentation={0}
        defaultCollapseIcon={
          <ExpandMoreIcon
            sx={{
              color: chevronColor,
              fontSize: "0.95rem",
            }}
          />
        }
        expandedItems={
          Array.isArray(expandedItems) ? expandedItems.map(String) : []
        }
        onExpandedItemsChange={(_event, itemIds) =>
          setExpandedItems(Array.isArray(itemIds) ? itemIds : [])
        }
        selectedItems={selectedId}
        onSelectedItemsChange={(_event, itemId) => {
          if (itemId != null) setSelectedItems(itemId);
        }}
        defaultExpandIcon={
          <ChevronRightIcon
            sx={{
              color: chevronColor,
              fontSize: "0.95rem",
            }}
          />
        }
      >
        {renderTree(data)}
      </SimpleTreeView>
    </Box>
  );
}
